import { type ActionClient, type ActionClientOptions, createActionClient } from "@adea/actions/client";
import type { AnyActionSpec, ObjectRef, SpecMap } from "@adea/actions/spec";
import { type UseQueryOptions, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { askToConfirm } from "./confirm.tsx";

/**
 * An action client whose specs arrive on demand (see `actions.ts`). `loaded(name)` is the real spec once it is
 * here; `ensure(name)` starts fetching what the action needs and resolves when it has arrived.
 *
 * Until then the client works with a stand-in: a call needs no spec (it is one request), a read's live-channel
 * refs are worked out when something changes (not when the read is made), and an instant update simply waits
 * for the next render, which comes when the spec has arrived.
 */
export interface SpecSource {
  loaded(name: string): AnyActionSpec | undefined;
  ensure(name: string): Promise<void>;
}

type Options = Omit<ActionClientOptions<Record<string, AnyActionSpec>>, "specs" | "onConfirmationRequired">;

export function lazyClient<M extends SpecMap>(
  source: SpecSource,
  options: Options,
  confirmOwn: (name: string, input: unknown, typed?: string) => Promise<string>,
): ActionClient<M> {
  type Client = ActionClient<M>;

  /** What the client knows of an action that hasn't arrived: enough to be called, nothing it would apply. */
  const standIns = new Map<string, AnyActionSpec>();
  const standIn = (name: string): AnyActionSpec => {
    let one = standIns.get(name);
    if (!one) {
      one = {
        name,
        kind: "read",
        risk: "none",
        input: { safeParse: () => ({ success: false }) },
      } as unknown as AnyActionSpec;
      standIns.set(name, one);
    }
    return one;
  };

  /** The client's view of the specs: the real one when it has arrived, the stand-in (and a request to fetch it) until then. */
  const specs = new Proxy({} as Record<string, AnyActionSpec>, {
    get(_target, key) {
      if (typeof key !== "string") return undefined;
      const real = source.loaded(key);
      if (real) return real;
      void source.ensure(key);
      return standIn(key);
    },
  });

  /** What a read touches, as the spec says (once the spec is here); until then, "anything of this action". */
  const refsOf = (name: string, input: unknown): ObjectRef[] => {
    const real = source.loaded(name);
    if (!real) return [{ type: name }];
    const parsed = real.input.safeParse(input);
    return parsed.success && real.reads ? real.reads(parsed.data) : [{ type: name }];
  };

  const base = createActionClient({
    ...options,
    specs,
    onConfirmationRequired: (request, input) =>
      askToConfirm(request, (typed) => confirmOwn(request.action, input, typed)),
  }) as unknown as Client;

  const queryOptions = ((name: string, input: unknown) => {
    const read = (base.queryOptions as (n: string, i: unknown) => Record<string, unknown>)(name, input);
    if (source.loaded(name)) return read;
    // The spec arrives after the read was made: what it touches is worked out when a change asks.
    return {
      ...read,
      meta: {
        get refs() {
          return refsOf(name, input);
        },
      },
    };
  }) as unknown as Client["queryOptions"];

  const useActionQuery = ((name: string, input: unknown, extra: object = {}) =>
    useQuery({
      ...(queryOptions as (n: string, i: unknown) => UseQueryOptions)(name, input),
      ...extra,
    })) as unknown as Client["useActionQuery"];

  const useAction = ((name: string, extra?: object) => {
    // When the spec arrives, draw again: the change then has its instant update and its exact refetch.
    const [, again] = useState(0);
    useEffect(() => {
      if (!source.loaded(name)) void source.ensure(name).then(() => again((n) => n + 1));
    }, [name]);
    return (base.useAction as (n: string, e?: object) => unknown)(name, extra);
  }) as unknown as Client["useAction"];

  return { ...base, queryOptions, useActionQuery, useAction };
}
